import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "hiddenInput",
    "intervalMode", "cronMode",
    "intervalRadio", "cronRadio",
    "dayOfMonthSelect",
    "cronExpressionInput",
    "summary"
  ]

  static values = {
    initialValue: String
  }

  connect() {
    this.dayButtons = Array.from(this.element.querySelectorAll('[data-action="click->cron-scheduler#toggleDay"]'))
    this.hourButtons = Array.from(this.element.querySelectorAll('[data-action="click->cron-scheduler#toggleHour"]'))
    if (this.initialValueValue) {
      this.parseCron(this.initialValueValue)
      this.updateCron()
    }
  }

  toggleMode(event) {
    if (event.currentTarget.value === "interval") {
      this.intervalModeTarget.classList.remove("hidden")
      this.cronModeTarget.classList.add("hidden")
      this.updateCron()
    } else {
      this.intervalModeTarget.classList.add("hidden")
      this.cronModeTarget.classList.remove("hidden")
      this.cronExpressionInputTarget.value = this.hiddenInputTarget.value
    }
  }

  toggleDay(event) {
    const clickedDay = event.currentTarget
    const dayValue = clickedDay.dataset.day
    const allDaysButton = this.dayButtons.find(btn => btn.dataset.day === "*")

    if (dayValue === "*") {
      // If "All Days" is clicked, activate it and deactivate others.
      this.activate(allDaysButton)
      this.dayButtons.forEach(button => {
        if (button !== allDaysButton) this.deactivate(button)
      })
    } else {
      // If a specific day is clicked, deactivate "All Days" and toggle the day.
      this.deactivate(allDaysButton)
      this.toggle(clickedDay)
    }

    // Check if any specific day is selected.
    const selectedDays = this.dayButtons.filter(btn => btn.dataset.day !== "*" && btn.classList.contains("bg-purple-500"))
    if (selectedDays.length === 0) {
      // If no specific days are selected, activate "All Days".
      this.activate(allDaysButton)
    }

    this.updateCron()
  }

  toggleHour(event) {
    this.toggle(event.currentTarget)
    this.updateCron()
  }

  updateCron() {
    if (this.cronRadioTarget.checked) {
      return;
    }
    const dayOfMonth = Array.from(this.dayOfMonthSelectTarget.selectedOptions).map(opt => opt.value).join(",") || "*"
    const days = this.getSelectedValues(this.dayButtons)
    const hours = this.getSelectedValues(this.hourButtons)

    const dayOfWeek = days.includes("*") ? "*" : days.join(",")
    const hour = hours.length === 0 || hours.length === 24 ? "*" : hours.join(",")

    const cron = `0 ${hour} ${dayOfMonth} * ${dayOfWeek}`

    this.hiddenInputTarget.value = cron
    this.cronExpressionInputTarget.value = cron
    if (this.hasSummaryTarget) {
      this.summaryTarget.dataset.cronSummaryCronValue = cron
    }
  }

  updateFromCronInput() {
    const cron = this.cronExpressionInputTarget.value
    this.hiddenInputTarget.value = cron
    if (this.hasSummaryTarget) {
      this.summaryTarget.dataset.cronSummaryCronValue = cron
    }
  }

  parseCron(cron) {
    this.hiddenInputTarget.value = cron
    this.cronExpressionInputTarget.value = cron
    const parts = cron.split(" ")

    if (parts.length !== 5 || cron.includes("/") || parts[0] !== '0') {
      this.intervalModeTarget.classList.add("hidden")
      this.cronModeTarget.classList.remove("hidden")
      this.cronRadioTarget.checked = true
      this.intervalRadioTarget.checked = false
      if (this.hasSummaryTarget) {
        this.summaryTarget.dataset.cronSummaryCronValue = cron
      }
      return
    }

    this.intervalModeTarget.classList.remove("hidden")
    this.cronModeTarget.classList.add("hidden")
    this.intervalRadioTarget.checked = true
    this.cronRadioTarget.checked = false

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts

    setTimeout(() => {
      const tomSelect = this.dayOfMonthSelectTarget.tomselect
      if (tomSelect) {
        if (dayOfMonth !== "*") {
          tomSelect.setValue(dayOfMonth.split(","))
        } else {
          tomSelect.clear()
        }
      } else {
        Array.from(this.dayOfMonthSelectTarget.options).forEach(opt => {
          opt.selected = (dayOfMonth !== "*" && dayOfMonth.split(",").includes(opt.value))
        })
      }
    }, 100)

    this.dayButtons.forEach(b => this.deactivate(b))
    if (dayOfWeek === "*") {
      this.activate(this.dayButtons.find(btn => btn.dataset.day === "*"))
    } else {
      this.deactivate(this.dayButtons.find(btn => btn.dataset.day === "*"))
      const days = dayOfWeek.split(",")
      days.forEach(d => {
        const button = this.dayButtons.find(btn => btn.dataset.day === d)
        if (button) this.activate(button)
      })
    }

    this.hourButtons.forEach(b => this.deactivate(b))
    if (hour !== "*") {
      const hours = hour.split(",")
      hours.forEach(h => {
        const button = this.hourButtons.find(btn => btn.dataset.hour === h)
        if (button) this.activate(button)
      })
    }

    if (this.hasSummaryTarget) {
      this.summaryTarget.dataset.cronSummaryCronValue = cron
    }
  }

  // Helper methods
  getSelectedValues(buttons) {
    return buttons
        .filter(btn => btn.classList.contains("bg-purple-500"))
        .map(btn => btn.dataset.day || btn.dataset.hour)
  }

  activate(element) {
    element.classList.add("bg-purple-500", "text-white", "border-purple-500")
    element.classList.remove("bg-white", "text-gray-700", "border-gray-300")
  }

  deactivate(element) {
    element.classList.remove("bg-purple-500", "text-white", "border-purple-500")
    element.classList.add("bg-white", "text-gray-700", "border-gray-300")
  }

  toggle(element) {
    if (element.classList.contains("bg-purple-500")) {
      this.deactivate(element)
    } else {
      this.activate(element)
    }
  }
}
